/**
 * "New booking" from the chat (boona repo: docs/codebase/inbox/README.md §7.7).
 *
 * A booking needs a slot, and only the calendar knows slots — so the chat hands the
 * client over, the journal shows "choose a time for <client>", and the next free-slot
 * click opens the create modal with that client already filled in.
 * AppointmentCreateModalProvider takes the client from here when a cell opens it.
 */
import { useSyncExternalStore } from "react";
import type { BookingClient } from "./crossTab";

let pending: BookingClient | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

export function setPendingBooking(client: BookingClient | null): void {
  pending = client;
  emit();
}

export function getPendingBooking(): BookingClient | null {
  return pending;
}

/** The waiting client, consumed: one slot click books one visit. */
export function takePendingBooking(): BookingClient | null {
  const client = pending;
  if (client) {
    pending = null;
    emit();
  }
  return client;
}

export function subscribePendingBooking(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function usePendingBooking(): BookingClient | null {
  return useSyncExternalStore(subscribePendingBooking, getPendingBooking, getPendingBooking);
}

/** The create modal's client object (AppointmentRegistry.createDraftAppointment defaults). */
export function draftClientFor(client: BookingClient) {
  return {
    type: "client",
    id: client.id,
    // the card is chosen by id (admin bookings never re-resolve by name/phone), so
    // the display name goes in whole rather than guessed apart into name/surname
    name: client.name,
    surname: "",
    patronymic: "",
    // a masked phone (no phones permission) must never reach the form
    phone: client.phone && !client.phone.includes("•") ? client.phone : "",
    additional_phone: "",
    email: "",
    comment: "",
    client_tips: false,
    birthday: null,
    card_number: "",
    gender: 0,
    discount: 0,
    discount_percent: 0,
    confirm_method: 1,
    client_tags: [],
  };
}
